import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUser } from '@/contexts/UserContext';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import { getBaseUrl } from '@/utils/api';

const API_URL = getBaseUrl();

const WelcomePage = () => {
  const navigate = useNavigate();
  const { user, login } = useUser();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check for auth message and auto-redirect
  useEffect(() => {
    // Check for message from auth callback
    const authMessage = sessionStorage.getItem('auth_message');
    if (authMessage) {
      setError(authMessage);
      sessionStorage.removeItem('auth_message');
    }
    
    // Auto-redirect if already logged in
    if (user) {
      if (user.role === 'master_admin') {
        navigate('/admin/dashboard', { replace: true });
      } else if (user.role === 'coach') {
        navigate('/coach/dashboard', { replace: true });
      } else {
        navigate('/client/hub', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    
    // Use XMLHttpRequest to avoid any fetch interceptors
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/auth/login`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.withCredentials = true;
    
    xhr.onload = function() {
      setLoading(false);
      try {
        const data = JSON.parse(xhr.responseText);
        
        if (xhr.status >= 400) {
          setError(data.detail || 'Login failed');
          return;
        }
        
        if (!data.user) {
          setError('Invalid response from server');
          return;
        }
        
        // Save user and token
        login(data.user, data.access_token);
        
        // Navigate based on role
        if (data.user.role === 'master_admin') {
          navigate('/admin/dashboard');
        } else if (data.user.role === 'coach') {
          navigate('/coach/dashboard');
        } else {
          navigate('/client/hub');
        }
      } catch (parseError) {
        setError('Login failed. Please try again.');
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      setError('Network error. Please try again.');
    };
    
    xhr.send(JSON.stringify(formData));
  };

  return (
    <div className="min-h-screen bg-[#F5F2EB] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Brand Header */}
        <div className="text-center mb-8">
          <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-[#1a1a1a] mb-3">
            GNTL STRNGTH
          </h1>
          <p className="text-[#1A1A1A] font-sans text-base">Welcome back</p>
        </div>

        {/* Sign In Card */}
        <Card className="bg-white border-0 shadow-lg rounded-2xl" data-testid="signin-card">
          <CardHeader className="pb-4 pt-8 px-8">
            <CardTitle className="font-serif text-[#1a1a1a] text-center text-2xl" data-testid="signin-title">
              Sign In
            </CardTitle>
            <CardDescription className="text-[#2D3B2D] font-sans text-center text-sm mt-2">
              Enter your credentials to continue
            </CardDescription>
          </CardHeader>
          
          <CardContent className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#1a1a1a] font-sans text-sm font-medium">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#2D3B2D]" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 bg-[#F5F2EB] border-[#F5F2EB] focus:border-[#C45A3B] focus:ring-[#C45A3B] text-[#1a1a1a] rounded-lg h-11"
                    data-testid="email-input"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-[#1a1a1a] font-sans text-sm font-medium">
                    Password
                  </Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-xs text-[#C45A3B] hover:underline font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#2D3B2D]" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 bg-[#F5F2EB] border-[#F5F2EB] focus:border-[#C45A3B] focus:ring-[#C45A3B] text-[#1a1a1a] rounded-lg h-11"
                    data-testid="password-input"
                  />
                </div>
                <p className="text-xs text-[#2D3B2D] mt-1">
                  First time? Use your email as password.
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="text-red-600 text-sm font-sans text-center bg-red-50 p-3 rounded-lg" data-testid="error-message">
                  {error}
                </div>
              )}

              {/* Sign In Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#C45A3B] text-white hover:bg-[#C45A3B] font-semibold h-12 rounded-lg text-base transition-colors"
                data-testid="signin-button"
              >
                {loading ? 'Signing in...' : 'Sign In'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>

            {/* Sign Up Link */}
            <p className="text-center text-sm text-[#2D3B2D] font-sans mt-6">
              Don't have an account?{' '}
              <Link 
                to="/signup" 
                className="text-[#C45A3B] hover:underline font-semibold"
                data-testid="signup-link"
              >
                Sign up
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WelcomePage;
